import type { IApiResponse } from "../../../services/api";
import { Api } from "../../../services/interceptor";
import { QUERY_KEY } from "../../../services/queryEndPoint";
import {
  normalizePitchTypeForSchedule,
  normalizePlayoffTypeForSchedule,
} from "../../leaguesHome/tournaments/tournamentsDetails/matches/scheduleMatchEnums";
import type { ICreateLeagueRequest, ILeagueItem } from "../types/myLeagues";
import type { IGetAllLeagues, ILeagueDetails } from "../../../components/header/types/header";
import { normalizeGroundTimingOpen } from "../utils/groundPayload";

type ApiPayload<T> = T | IApiResponse<T>;

const unwrapApiPayload = <T>(payload: ApiPayload<T>): T => {
  if (payload && typeof payload === "object" && "data" in payload) {
    return (payload as IApiResponse<T>).data;
  }
  return payload as T;
};

const asRecord = (value: unknown): Record<string, unknown> | null =>
  typeof value === "object" && value !== null
    ? (value as Record<string, unknown>)
    : null;

const normalizeLeagueItem = (
  rec: Record<string, unknown>,
): ILeagueItem | null => {
  const rawLeagueId = rec.leagueId ?? rec.id ?? rec.leagueID ?? rec.league_id;
  if (!rawLeagueId) return null;
  return {
    leagueId: String(rawLeagueId),
    leagueName: String(rec.leagueName ?? rec.name ?? rec.league_name ?? ""),
    organizerName:
      rec.organizerName != null ? String(rec.organizerName) : undefined,
    organizerEmailId:
      rec.organizerEmailId != null ? String(rec.organizerEmailId) : undefined,
    organizerPhoneNumber:
      rec.organizerPhoneNumber != null
        ? String(rec.organizerPhoneNumber)
        : undefined,
    imageUri: rec.imageUri != null ? String(rec.imageUri) : undefined,
    bannerUri: rec.bannerUri != null ? String(rec.bannerUri) : undefined,
    latestTournamentId:
      rec.latestTournamentId != null
        ? String(rec.latestTournamentId)
        : undefined,
    tournamentIds: Array.isArray(rec.tournamentIds)
      ? rec.tournamentIds.map(String)
      : [],
    roleName: rec.roleName != null ? String(rec.roleName) : undefined,
    city: rec.city != null ? String(rec.city) : undefined,
    state: rec.state != null ? String(rec.state) : undefined,
    country: rec.country != null ? String(rec.country) : undefined,
    association: rec.association != null ? String(rec.association) : undefined,
    countryCode:
      typeof rec.countryCode === "number" ? rec.countryCode : undefined,
    groundsRegistered:
      typeof rec.groundsRegistered === "number"
        ? rec.groundsRegistered
        : undefined,
    sponsorsRegistered:
      typeof rec.sponsorsRegistered === "number"
        ? rec.sponsorsRegistered
        : undefined,
    allowPlayersToJoin:
      rec.allowPlayersToJoin === true || rec.allowPlayersToJoin === "true",
    canEditLeague: rec.canEditLeague === true,
    canAddTournamentToLeague: rec.canAddTournamentToLeague === true,
    canAddTeamToLeague: rec.canAddTeamToLeague === true,
    canAddPlayerToTeam: rec.canAddPlayerToTeam === true,
    canCreateDivisions: rec.canCreateDivisions === true,
    startDate: rec.startDate != null ? String(rec.startDate) : undefined,
    venue: rec.venue != null ? String(rec.venue) : undefined,
    teamsCount: typeof rec.teamsCount === "number" ? rec.teamsCount : undefined,
    oversFormat: rec.oversFormat != null ? String(rec.oversFormat) : undefined,
  };
};

const extractLeaguesArray = (payload: unknown): ILeagueItem[] => {
  const payloadRecord = asRecord(payload);
  const nestedData = payloadRecord ? asRecord(payloadRecord.data) : null;

  const tryExtract = (arr: unknown): ILeagueItem[] => {
    if (!Array.isArray(arr)) return [];
    return arr
      .map((item) => {
        const rec = asRecord(item);
        return rec ? normalizeLeagueItem(rec) : null;
      })
      .filter((l): l is ILeagueItem => l != null);
  };

  if (Array.isArray(payload)) return tryExtract(payload);
  if (payloadRecord?.leagues != null) return tryExtract(payloadRecord.leagues);
  if (payloadRecord?.leagueResponseList != null)
    return tryExtract(payloadRecord.leagueResponseList);
  if (payloadRecord?.data != null && Array.isArray(payloadRecord.data))
    return tryExtract(payloadRecord.data);
  if (nestedData?.leagueResponseList != null)
    return tryExtract(nestedData.leagueResponseList);
  if (nestedData?.leagues != null) return tryExtract(nestedData.leagues);

  return [];
};

export const fetchAllLeagues = async (): Promise<ILeagueItem[]> => {
  const { data } = await Api.get<unknown>(QUERY_KEY.getAllLeagues);
  const raw = unwrapApiPayload(data);
  return extractLeaguesArray(raw ?? data);
};

/**
 * POST /league — multipart/form-data.
 * Server expects flat string fields plus required `image` (logo) and `bannerUri` files.
 */
export const createLeague = async (
  payload: ICreateLeagueRequest,
  logoFile: File,
  bannerFile: File,
): Promise<string> => {
  const formData = new FormData();
  formData.append("leagueName", payload.leagueName);
  formData.append("organizerName", payload.organizerName);
  formData.append("organizerPhoneNumber", payload.organizerPhoneNumber);
  formData.append("city", payload.city);
  formData.append("state", payload.state);
  formData.append("country", payload.country);
  formData.append("countryCode", String(payload.countryCode));
  formData.append("allowPlayersToJoin", String(payload.allowPlayersToJoin));
  if (payload.organizerEmailId) {
    formData.append("organizerEmailId", payload.organizerEmailId);
  }
  if (payload.association) {
    formData.append("association", payload.association);
  }
  formData.append("image", logoFile);
  formData.append("bannerUri", bannerFile);

  const { data } = await Api.post<ApiPayload<string>>(
    QUERY_KEY.createLeague,
    formData,
  );
  const result = unwrapApiPayload(data);
  return typeof result === "string" ? result : String(result ?? "");
};

export const fetchLeagueById = async (
  leagueId: string,
): Promise<ILeagueDetails> => {
  const { data } = await Api.get<unknown>(
    `${QUERY_KEY.getLeagueDetails}/${leagueId}`,
  );
  const raw = unwrapApiPayload(data);
  if (raw && typeof raw === "object") {
    return raw as ILeagueDetails;
  }
  return data as ILeagueDetails;
};

/**
 * PUT /updateLeague — multipart/form-data.
 * Required: leagueId. Other fields and files are optional per API.
 */
export const updateLeagueRequest = async (args: {
  leagueId: string;
  leagueName: string;
  organizerName: string;
  organizerPhoneNumber: string;
  organizerEmailId: string;
  city: string;
  state: string;
  country: string;
  countryCode: number;
  association: string;
  allowPlayersToJoin: boolean;
  imageFile?: File | null;
  bannerFile?: File | null;
}): Promise<void> => {
  const formData = new FormData();

  formData.append("leagueId", args.leagueId);
  formData.append("leagueName", args.leagueName.trim());
  formData.append("organizerName", args.organizerName.trim());
  formData.append(
    "organizerPhoneNumber",
    args.organizerPhoneNumber.replace(/\D/g, "").slice(-10) ||
      args.organizerPhoneNumber.trim(),
  );
  formData.append("organizerEmailId", args.organizerEmailId.trim());
  formData.append("city", args.city.trim());
  formData.append("state", args.state.trim());
  formData.append("country", args.country.trim());
  formData.append("association", args.association.trim());
  formData.append("countryCode", String(args.countryCode));
  formData.append("allowPlayersToJoin", String(args.allowPlayersToJoin));

  if (args.imageFile) {
    formData.append("image", args.imageFile);
  }
  if (args.bannerFile) {
    formData.append("bannerUri", args.bannerFile);
  }

  await Api.put(QUERY_KEY.updateLeague, formData);
};

/** Ground item for bulk upload - matches PutGroundRequest on the server. */
export interface IBulkGroundItem {
  groundId?: string;
  groundName?: string;
  groundAddress?: string;
  groundDescription?: string;
  contactNumber?: string;
  email?: string;
  gMapLink?: string;
  daysOpen?: string;
  timingOpen?: string;
  groundFacilities?: string;
  matchesPlayedTillDate?: number;
}

export interface IBulkUploadGroundRequest {
  grounds: IBulkGroundItem[];
}

/** Valid day names - daysOpen accepts a single day, "Everyday", or a comma-separated list of days. */
const VALID_DAYS = new Set(
  "sunday,monday,tuesday,wednesday,thursday,friday,saturday".split(","),
);

const isValidDaysOpen = (raw: string): boolean => {
  const lc = raw.toLowerCase();
  if (lc === "everyday") return true;
  const parts = lc.split(",").map((p) => p.trim()).filter(Boolean);
  if (parts.length === 0) return false;
  return parts.every((p) => VALID_DAYS.has(p));
};

/** Strip and sanitize ground item. API expects valid formats (digits-only contact, day names, normalized timing strings). */
const cleanGroundItem = (g: IBulkGroundItem): IBulkGroundItem => {
  const out: IBulkGroundItem = {};
  for (const [k, v] of Object.entries(g)) {
    if (v === undefined || v === null) continue;
    if (typeof v === "object") continue;
    if (typeof v === "number" && Number.isNaN(v)) continue;

    if (typeof v === "string") {
      const trimmed = v.trim();
      if (trimmed === "") continue;
      if (k === "daysOpen" && !isValidDaysOpen(trimmed)) continue;
      if (k === "contactNumber") {
        const digits = trimmed.replace(/\D/g, "");
        if (digits.length === 0) continue;
        const contactNumber = digits.length >= 10 ? digits.slice(-10) : digits;
        (out as Record<string, unknown>)[k] = contactNumber;
        continue;
      }
      if (k === "timingOpen") {
        const timingOpen = normalizeGroundTimingOpen(trimmed);
        if (!timingOpen) continue;
        (out as Record<string, unknown>)[k] = timingOpen;
        continue;
      }
      (out as Record<string, unknown>)[k] = trimmed;
      continue;
    }
    (out as Record<string, unknown>)[k] = v;
  }
  return out;
};

/** Ensure ground has required API fields. imageUri/imageBucketLink intentionally omitted for now. */
const ensureRequiredPayloadFields = (g: IBulkGroundItem): IBulkGroundItem => ({
  ...g,
  gMapLink: g.gMapLink ?? "",
  groundFacilities: g.groundFacilities ?? "",
  matchesPlayedTillDate: g.matchesPlayedTillDate ?? 0,
});

export const bulkUploadGround = async (
  leagueId: string,
  payload: IBulkUploadGroundRequest,
): Promise<string> => {
  const grounds = payload.grounds
    .map(cleanGroundItem)
    .map(ensureRequiredPayloadFields);
  const body = { grounds };
  try {
    const { data } = await Api.post<ApiPayload<string>>(
      `${QUERY_KEY.bulkUploadGround}/${leagueId}`,
      body,
      { headers: { "Content-Type": "application/json" } },
    );
    const result = unwrapApiPayload(data);
    return typeof result === "string" ? result : String(result ?? "");
  } catch (err) {
    const axiosErr = err as {
      response?: {
        status?: number;
        data?: unknown;
        config?: { data?: string };
      };
    };
    const status = axiosErr?.response?.status;
    const resData = axiosErr?.response?.data;
    const requestPayload = axiosErr?.response?.config?.data;
    console.error("[bulkUploadGround] API failed:", {
      status,
      url: `${QUERY_KEY.bulkUploadGround}/${leagueId}`,
      responseData: resData,
      requestPayload: requestPayload ?? body,
    });
    throw err;
  }
};

/** Team snapshot for bulk division/team create - per API spec */
export interface IBulkTeamSnapshotPutRequest {
  teamId?: string | null;
  /** Tournament roster id when attaching an existing enrolled team */
  teamSnapshotId?: string | null;
  teamName?: string;
  teamCode?: string;
  captainPlayerId?: string;
  viceCaptainPlayerId?: string;
  wkPlayerId?: string;
  tournamentId?: string;
  leagueId?: string;
  image?: unknown;
  description?: string;
  groundId?: string;
}

/** Division request for bulk create - per API spec */
export interface IBulkDivisionRequest {
  tournamentId?: string;
  divisionName?: string;
  divisionId?: string;
  subDivisions?: { divisionRequests: IBulkDivisionRequest[] } | null;
  teamSnapshotPutRequestList?: {
    teamSnapshotPutRequestList: IBulkTeamSnapshotPutRequest[];
  } | null;
}

export interface IBulkDivisionUploadRequest {
  divisionRequests: IBulkDivisionRequest[];
}

/** Player request for bulk upload - subset of API playerRequestList fields */
export interface IBulkPlayerRequest {
  playerFirstName?: string | null;
  playerLastName?: string | null;
  playerMiddleName?: string | null;
  playerRole?: string | null;
  DOB?: string | null;
  dob?: string | null;
  emailId?: string | null;
  phoneNo?: string | null;
  battingStyle?: string | null;
  bowlingStyle?: string | null;
  gender?: string | null;
  captain?: boolean | null;
  wicketKeeper?: boolean | null;
  countryCode?: number | null;
  cricHeroesPlayerId?: string | null;
  currency?: string | null;
  desc?: string | null;
  emergencyContactRelation?: string | null;
  emergencyName?: string | null;
  emergencyNo?: string | null;
  experience?: number | null;
  fees?: number | null;
  frequency?: string | null;
  image?: {
    empty?: boolean;
    present?: boolean;
  } | null;
  imageUri?: string | null;
  isEmailEnabled?: boolean | null;
  isPartOfLeague?: boolean | null;
  isScorer?: boolean | null;
  isSmsEnabled?: boolean | null;
  isUmpire?: boolean | null;
  locations?: string[] | null;
  name?: string | null;
  playerExtracted?: boolean | null;
  playerId?: string | null;
  playerProfileStatus?: string | null;
  scorerDetails?: Record<string, unknown> | null;
  stAddress?: string | null;
  state?: string | null;
  userName?: string | null;
  userStatus?: string | null;
  zipCode?: string | null;
  [key: string]: unknown;
}

export interface IPlayerForTeamsItem {
  teamSnapshotId: string;
  playerRequestList: IBulkPlayerRequest[];
}

export interface IBulkPlayersForTeamRequest {
  playerForTeamsList: IPlayerForTeamsItem[];
}

const normalizePlayerRole = (player: IBulkPlayerRequest): string => {
  const explicitRole =
    typeof player.playerRole === "string" ? player.playerRole.trim() : "";
  if (explicitRole) {
    if (explicitRole === "Wicket Keeper") return "WICKET_KEEPER";
    if (explicitRole === "All Rounder") return "ALL_ROUNDER";
    if (explicitRole === "Vice Captain") return "VICE_CAPTAIN";
    return explicitRole.toUpperCase().replace(/\s+/g, "_");
  }
  if (player.captain === true) return "CAPTAIN";
  if (player.wicketKeeper === true) return "WICKET_KEEPER";
  return "BATSMAN";
};

const normalizeNullableString = (value: unknown): string | null => {
  if (value == null) return null;
  const trimmed = String(value).trim();
  return trimmed === "" ? null : trimmed;
};

const normalizeNullableNumber = (value: unknown): number | null => {
  if (value == null || value === "") return null;
  const numeric = Number(value);
  return Number.isNaN(numeric) ? null : numeric;
};

const normalizePlayerRequest = (
  player: IBulkPlayerRequest,
): Record<string, unknown> => ({
  playerId: normalizeNullableString(player.playerId),
  gender: normalizeNullableString(player.gender),
  emailId: normalizeNullableString(player.emailId),
  phoneNo: normalizeNullableString(player.phoneNo),
  countryCode: normalizeNullableNumber(player.countryCode),
  battingStyle: normalizeNullableString(player.battingStyle),
  bowlingStyle: normalizeNullableString(player.bowlingStyle),
  emergencyName: normalizeNullableString(player.emergencyName),
  emergencyNo: normalizeNullableString(player.emergencyNo),
  stAddress: normalizeNullableString(player.stAddress),
  zipCode: normalizeNullableString(player.zipCode),
  state: normalizeNullableString(player.state),
  desc: normalizeNullableString(player.desc),
  playerFirstName: normalizeNullableString(player.playerFirstName) ?? "",
  playerLastName: normalizeNullableString(player.playerLastName) ?? "",
  playerMiddleName: normalizeNullableString(player.playerMiddleName),
  playerRole: normalizePlayerRole(player),
  imageUri: normalizeNullableString(player.imageUri),
  userStatus: normalizeNullableString(player.userStatus),
  dob: normalizeNullableString(player.dob ?? player.DOB),
  isPartOfLeague: player.isPartOfLeague ?? false,
  isUmpire: player.isUmpire ?? null,
  experience: normalizeNullableNumber(player.experience),
  fees: normalizeNullableNumber(player.fees) ?? 0,
  currency: normalizeNullableString(player.currency) ?? "INR",
  frequency: normalizeNullableString(player.frequency) ?? "HOUR",
  locations: Array.isArray(player.locations)
    ? player.locations.filter(Boolean).map(String)
    : [],
  isSmsEnabled: player.isSmsEnabled ?? false,
  isEmailEnabled: player.isEmailEnabled ?? false,
});

/** One POST body: each team has `teamId` + full `playerRequestList` (bulk add per team). */
export const bulkUploadPlayersForTeams = async (
  tournamentId: string,
  payload: IBulkPlayersForTeamRequest,
): Promise<string> => {
  const playerForTeamsList = payload.playerForTeamsList
    .map((team) => {
      const teamId = team.teamSnapshotId?.trim();
      if (!teamId) return null;

      const playerRequestList = (team.playerRequestList ?? [])
        .map((player) => normalizePlayerRequest(player))
        .filter((row) => {
          const first = String(row.playerFirstName ?? "").trim();
          const last = String(row.playerLastName ?? "").trim();
          return Boolean(first || last);
        });

      if (playerRequestList.length === 0) return null;

      return { teamId, playerRequestList };
    })
    .filter(
      (
        entry,
      ): entry is {
        teamId: string;
        playerRequestList: Record<string, unknown>[];
      } => entry != null,
    );

  if (playerForTeamsList.length === 0) {
    throw new Error(
      "No valid teams with players to upload. Ensure each team has a team snapshot ID and at least one player.",
    );
  }

  const requestPayload = { playerForTeamsList };

  try {
    const { data } = await Api.post<ApiPayload<string>>(
      `${QUERY_KEY.bulkUploadPlayersForTeams}/${tournamentId}`,
      requestPayload,
      { headers: { "Content-Type": "application/json" } },
    );
    const result = unwrapApiPayload(data);
    return typeof result === "string" ? result : String(result ?? "");
  } catch (err) {
    const axiosErr = err as {
      response?: {
        status?: number;
        data?: { message?: string; error?: string } | string;
        config?: { data?: string };
      };
    };
    const responseData = axiosErr?.response?.data;
    const serverMessage =
      typeof responseData === "string"
        ? responseData
        : (responseData?.message ?? responseData?.error ?? null);
    console.error("[bulkUploadPlayersForTeams] API failed:", {
      status: axiosErr?.response?.status,
      url: `${QUERY_KEY.bulkUploadPlayersForTeams}/${tournamentId}`,
      serverMessage,
      responseData,
      requestPayload,
    });
    throw err;
  }
};

export const bulkDivisionUpload = async (
  payload: IBulkDivisionUploadRequest,
): Promise<string> => {
  try {
    const { data } = await Api.post<ApiPayload<string>>(
      QUERY_KEY.bulkDivisionUpload,
      payload,
      { headers: { "Content-Type": "application/json" } },
    );
    const result = unwrapApiPayload(data);
    return typeof result === "string" ? result : String(result ?? "");
  } catch (err) {
    const axiosErr = err as {
      response?: {
        status?: number;
        data?: unknown;
        config?: { data?: string };
      };
    };
    console.error("[bulkDivisionUpload] API failed:", {
      status: axiosErr?.response?.status,
      url: QUERY_KEY.bulkDivisionUpload,
      responseData: axiosErr?.response?.data,
      requestPayload: axiosErr?.response?.config?.data ?? payload,
    });
    throw err;
  }
};

/** Slot info for auto schedule */
export interface ISlotInformation {
  endTime?: string;
  slotName?: string;
  startTime?: string;
}

/** createAutoSchedule request body - per API spec */
export interface IAutoScheduleRequest {
  days?: string;
  divisionId?: string;
  endDate?: string;
  homeAndAway?: boolean;
  maxMatchesPerTeamPerDay?: number;
  numberOfRounds?: number;
  parentDivisionId?: string;
  playoffSchedulingAlgorithm?: string;
  slotsInformation?: ISlotInformation[];
  startDate?: string;
  superNValue?: number;
  timeZone?: string;
  tournamentScheduleType?: string;
}

/** createAutoSchedule response - returns download URL for schedule file */
export interface ICreateAutoScheduleResponse {
  cloudfrontURL?: string;
  fileName?: string;
  /** Direct download URL; prefer this when present */
  downloadUrl?: string;
  fileUrl?: string;
  scheduleUrl?: string;
}

/** Match item for bulk create */
export interface IMatchBulkItem {
  team1Id?: string;
  team2Id?: string;
  team1SnapshotId?: string;
  team2SnapshotId?: string;
  divisionId?: string;
  groupId?: string;
  groundId?: string;
  matchDate?: string;
  matchTime?: string;
  [key: string]: unknown;
}

/** API request for createAndScheduleBulkMatches - matches /matches/bulk */
export interface IBulkScheduleMatchRequest {
  city?: string;
  groundName?: string;
  matchDate?: string;
  pitchType?: string;
  startTime?: string;
  teamOneName?: string;
  teamTwoName?: string;
  timeZone?: string;
  umpireName?: string;
  utcMatchTime?: string;
}

export interface IBulkScheduleRequest {
  divisionId: string;
  matchRequestList: IBulkScheduleMatchRequest[];
}

export const createAutoSchedule = async (
  body: IAutoScheduleRequest,
): Promise<ICreateAutoScheduleResponse> => {
  if (typeof body !== "object" || body === null) {
    console.error(
      "[createAutoSchedule] Invalid body - expected object, got:",
      typeof body,
    );
    throw new Error("createAutoSchedule requires a valid request object");
  }
  const payload: Record<string, unknown> = {};
  const fields: (keyof IAutoScheduleRequest)[] = [
    "divisionId",
    "startDate",
    "endDate",
    "homeAndAway",
    "numberOfRounds",
    "slotsInformation",
    "days",
    "timeZone",
    "maxMatchesPerTeamPerDay",
    "parentDivisionId",
    "tournamentScheduleType",
    "playoffSchedulingAlgorithm",
    "superNValue",
  ];
  for (const k of fields) {
    const v = body[k];
    if (v !== undefined) payload[k] = v;
  }
  const { data } = await Api.post<ApiPayload<ICreateAutoScheduleResponse>>(
    QUERY_KEY.createAutoSchedule,
    payload,
    { headers: { "Content-Type": "application/json" } },
  );
  const raw = unwrapApiPayload(data) as unknown;
  if (typeof raw === "string" && raw.trim()) {
    if (raw.startsWith("http")) {
      return { cloudfrontURL: "", fileName: "", downloadUrl: raw };
    }
    return {
      cloudfrontURL: "",
      fileName: raw.trim(),
      downloadUrl: "",
    };
  }
  const candidates: Record<string, unknown>[] = [
    (raw ?? {}) as Record<string, unknown>,
    (data ?? {}) as Record<string, unknown>,
  ];
  const nested =
    raw && typeof raw === "object" ? (raw as Record<string, unknown>) : null;
  if (nested) {
    if (nested.data && typeof nested.data === "object")
      candidates.push(nested.data as Record<string, unknown>);
    if (nested.result && typeof nested.result === "object")
      candidates.push(nested.result as Record<string, unknown>);
    if (nested.response && typeof nested.response === "object")
      candidates.push(nested.response as Record<string, unknown>);
    if (nested.body && typeof nested.body === "object")
      candidates.push(nested.body as Record<string, unknown>);
  }
  const extract = (obj: Record<string, unknown>) => ({
    cloudfrontURL:
      obj.cloudfrontURL ??
      obj.cloudfrontUrl ??
      obj.cloudFrontUrl ??
      obj.baseUrl ??
      obj.url ??
      "",
    fileName:
      obj.fileName ??
      obj.file_name ??
      obj.filename ??
      obj.downloadFileName ??
      obj.file ??
      "",
    directUrl:
      obj.downloadUrl ??
      obj.download_url ??
      obj.fileUrl ??
      obj.file_url ??
      obj.scheduleUrl ??
      obj.schedule_url ??
      obj.url ??
      "",
  });
  for (const obj of candidates) {
    const { cloudfrontURL, fileName, directUrl } = extract(obj);
    if (cloudfrontURL || fileName || directUrl) {
      return {
        cloudfrontURL: String(cloudfrontURL || ""),
        fileName: String(fileName || ""),
        downloadUrl: String(directUrl || ""),
      };
    }
  }
  if (import.meta.env.DEV) {
    console.warn(
      "[createAutoSchedule] Could not extract download info. Raw response:",
      data,
    );
  }
  return { cloudfrontURL: "", fileName: "", downloadUrl: "" };
};

export const matchesBulk = async (
  payload: IBulkScheduleRequest,
): Promise<string> => {
  const { data } = await Api.post<ApiPayload<string>>(
    QUERY_KEY.matchesBulk,
    payload,
    { headers: { "Content-Type": "application/json" } },
  );
  const result = unwrapApiPayload(data);
  return typeof result === "string" ? result : String(result ?? "");
};

/** Build ISO UTC from date (YYYY-MM-DD) + time (HH:mm) — same rules as ScheduleMatchStep. */
export function buildUtcMatchTimeFromDateAndTime(
  matchDate: string,
  startTime: string,
): string | undefined {
  if (!matchDate || !startTime) return undefined;
  const normalizedTime = /^\d{2}:\d{2}$/.test(startTime)
    ? `${startTime}:00`
    : startTime;
  const dateStr = matchDate.includes("T")
    ? matchDate
    : `${matchDate}T${normalizedTime}`;
  const parsed = new Date(dateStr);
  if (Number.isNaN(parsed.getTime())) return undefined;
  return parsed.toISOString();
}

export interface ICreateMatchRequest {
  /** Tournament roster ids — sent as `teamOneId` / `teamTwoId` in the JSON body. */
  teamOneSnapshotId: string;
  teamTwoSnapshotId: string;
  /** Omit, empty string, or null → JSON `null` */
  leagueId?: string | null;
  /** Omit, empty string, or null → JSON `null` */
  tournamentId?: string | null;
}

function optionalIdOrNull(value: string | null | undefined): string | null {
  if (value == null) return null;
  const t = String(value).trim();
  return t.length > 0 ? t : null;
}

/** POST `match` — response body is plain-text `matchId` (201). */
export async function createMatch(body: ICreateMatchRequest): Promise<string> {
  const teamOneId = body.teamOneSnapshotId.trim();
  const teamTwoId = body.teamTwoSnapshotId.trim();
  if (!teamOneId || !teamTwoId) {
    throw new Error("teamOneSnapshotId and teamTwoSnapshotId are required");
  }

  const payload: Record<string, string | null> = {
    teamOneId,
    teamTwoId,
    leagueId: optionalIdOrNull(body.leagueId),
    tournamentId: optionalIdOrNull(body.tournamentId),
  };

  const { data } = await Api.post<ApiPayload<string>>(
    QUERY_KEY.getMatchInfo,
    payload,
    { headers: { "Content-Type": "application/json" } },
  );
  const result = unwrapApiPayload(data);
  const id =
    typeof result === "string" ? result.trim() : String(result ?? "").trim();
  if (!id) throw new Error("Create match returned an empty id");
  return id;
}

/** POST /scheduleMatch — fields mirror Match Scheduling API (all optional except matchId). */
export interface IScheduleMatchRequest {
  matchId: string;
  city?: string | null;
  groundId?: string | null;
  matchType?: string | null;
  tournamentCategory?: string | null;
  divisionId?: string | null;
  maxOversPerBowler?: number | null;
  startDate?: string | null;
  pitchType?: string | null;
  isUmpire?: boolean;
  umpireId?: string | null;
  isScorer?: boolean;
  scorerId?: string | null;
  matchOvers?: number | null;
  playoffType?: string | null;
  umpireTwoId?: string | null;
  isUmpireTwoPerson?: boolean;
  thirdUmpireId?: string | null;
  isThirdUmpirePerson?: boolean;
  /** Send when user changed a team after the match was created. */
  teamOneSnapShotId?: string | null;
  teamTwoSnapShotId?: string | null;
}

function scheduleStrOrNull(v: string | null | undefined): string | null {
  if (v == null) return null;
  const t = String(v).trim();
  return t.length > 0 ? t : null;
}

function scheduleIntOrNull(v: number | null | undefined): number | null {
  if (v == null || Number.isNaN(v)) return null;
  if (!Number.isInteger(v)) return null;
  return v;
}

export function getScheduleMatchRequestErrorMessage(
  err: unknown,
  fallback: string,
): string {
  if (err && typeof err === "object" && "response" in err) {
    const data = (err as { response?: { data?: unknown } }).response?.data;
    if (typeof data === "string" && data.trim()) return data.trim();
    if (data && typeof data === "object" && "message" in data) {
      const m = (data as { message?: unknown }).message;
      if (typeof m === "string" && m.trim()) return m.trim();
      if (Array.isArray(m) && m.length > 0 && typeof m[0] === "string")
        return m[0];
    }
  }
  if (err instanceof Error && err.message.trim()) return err.message.trim();
  return fallback;
}

/** POST `scheduleMatch` — always sends the full field set; empty strings → `null`. */
export async function scheduleMatchRequest(
  body: IScheduleMatchRequest,
): Promise<string> {
  const matchId = body.matchId.trim();
  if (!matchId) throw new Error("scheduleMatch: matchId is required");

  const city = scheduleStrOrNull(body.city);
  const groundId = scheduleStrOrNull(body.groundId);
  const matchType = scheduleStrOrNull(body.matchType);
  const tournamentCategory = scheduleStrOrNull(body.tournamentCategory);
  const divisionId = scheduleStrOrNull(body.divisionId);
  const startDate = scheduleStrOrNull(body.startDate);
  const pitchTypeRaw = body.pitchType?.trim();
  const pitchType = pitchTypeRaw
    ? normalizePitchTypeForSchedule(body.pitchType!)
    : null;
  const playoffRaw = body.playoffType?.trim();
  const playoffType = playoffRaw
    ? normalizePlayoffTypeForSchedule(body.playoffType!)
    : null;

  const maxOversPerBowler = scheduleIntOrNull(
    body.maxOversPerBowler ?? undefined,
  );
  const matchOvers = scheduleIntOrNull(body.matchOvers ?? undefined);

  const umpireId = scheduleStrOrNull(body.umpireId);
  const umpireTwoId = scheduleStrOrNull(body.umpireTwoId);
  const thirdUmpireId = scheduleStrOrNull(body.thirdUmpireId);
  const scorerId = scheduleStrOrNull(body.scorerId);

  const isUmpire =
    body.isUmpire !== undefined ? Boolean(body.isUmpire) : umpireId != null;
  const isUmpireTwoPerson =
    body.isUmpireTwoPerson !== undefined
      ? Boolean(body.isUmpireTwoPerson)
      : umpireTwoId != null;
  const isThirdUmpirePerson =
    body.isThirdUmpirePerson !== undefined
      ? Boolean(body.isThirdUmpirePerson)
      : thirdUmpireId != null;
  const isScorer =
    body.isScorer !== undefined ? Boolean(body.isScorer) : scorerId != null;

  const teamOneSnapShotId = scheduleStrOrNull(body.teamOneSnapShotId);
  const teamTwoSnapShotId = scheduleStrOrNull(body.teamTwoSnapShotId);

  const payload: Record<string, unknown> = {
    matchId,
    city,
    groundId,
    matchType,
    tournamentCategory,
    divisionId,
    maxOversPerBowler,
    startDate,
    pitchType,
    isUmpire,
    umpireId,
    isScorer,
    scorerId,
    matchOvers,
    playoffType,
    umpireTwoId,
    isUmpireTwoPerson,
    thirdUmpireId,
    isThirdUmpirePerson,
  };
  if (teamOneSnapShotId) payload.teamOneSnapShotId = teamOneSnapShotId;
  if (teamTwoSnapShotId) payload.teamTwoSnapShotId = teamTwoSnapShotId;

  const { data } = await Api.post<ApiPayload<string>>(
    QUERY_KEY.scheduleMatch,
    payload,
    { headers: { "Content-Type": "application/json" } },
  );
  const result = unwrapApiPayload(data);
  return typeof result === "string"
    ? result.trim()
    : String(result ?? "").trim();
}

/**
 * Tournament “Schedule Match” (single): POST `match` then POST `scheduleMatch`.
 * Uses team snapshot ids on the wire as `teamOneId` / `teamTwoId`.
 */
export async function submitSingleMatchSchedule(params: {
  divisionId: string;
  teamOneSnapshotId: string;
  teamTwoSnapshotId: string;
  tournamentId?: string | null;
  leagueId?: string | null;
  matchDate: string;
  startTime: string;
  city: string;
  pitchType?: string;
  playoffType?: string;
  groundId?: string | null;
  matchType?: string | null;
  tournamentCategory?: string | null;
  matchOvers?: number | null;
  maxOversPerBowler?: number | null;
  umpireId?: string | null;
  umpireTwoId?: string | null;
  thirdUmpireId?: string | null;
  isUmpire?: boolean;
  isUmpireTwoPerson?: boolean;
  isThirdUmpirePerson?: boolean;
  scorerId?: string | null;
  isScorer?: boolean;
}): Promise<void> {
  const {
    divisionId,
    teamOneSnapshotId,
    teamTwoSnapshotId,
    tournamentId,
    leagueId,
    matchDate,
    startTime,
    city,
    pitchType,
    playoffType,
    groundId,
    matchType,
    tournamentCategory,
    matchOvers,
    maxOversPerBowler,
    umpireId,
    umpireTwoId,
    thirdUmpireId,
    isUmpire,
    isUmpireTwoPerson,
    isThirdUmpirePerson,
    scorerId,
    isScorer,
  } = params;

  const matchId = await createMatch({
    teamOneSnapshotId,
    teamTwoSnapshotId,
    tournamentId,
    leagueId,
  });

  const startDate = buildUtcMatchTimeFromDateAndTime(matchDate, startTime);
  if (!startDate) throw new Error("Invalid match date or start time");

  await scheduleMatchRequest({
    matchId,
    startDate,
    city,
    divisionId,
    pitchType: pitchType?.trim() || undefined,
    playoffType: playoffType?.trim() || undefined,
    groundId,
    matchType,
    tournamentCategory,
    matchOvers: matchOvers ?? undefined,
    maxOversPerBowler: maxOversPerBowler ?? undefined,
    umpireId,
    umpireTwoId,
    thirdUmpireId,
    isUmpire,
    isUmpireTwoPerson,
    isThirdUmpirePerson,
    scorerId,
    isScorer,
  });
}

/** Map ILeagueItem to IGetAllLeagues for Redux setSelectedLeague */
export const mapLeagueItemToGetAllLeagues = (
  item: ILeagueItem,
): IGetAllLeagues =>
  ({
    leagueId: item.leagueId,
    leagueName: item.leagueName,
    organizerName: item.organizerName ?? "",
    organizerEmailId: item.organizerEmailId ?? "",
    organizer: null,
    organizerPhoneNumber: item.organizerPhoneNumber ?? "",
    imageUri: item.imageUri ?? "",
    latestTournamentId: item.latestTournamentId ?? "",
    tournamentIds: item.tournamentIds ?? [],
    roleName: item.roleName ?? "",
    city: item.city ?? "",
    state: item.state ?? "",
    country: item.country ?? "",
    bannerUri: item.bannerUri ?? "",
    association: item.association ?? "",
    countryCode: item.countryCode ?? 0,
    canEditLeague: item.canEditLeague ?? false,
    canAddTournamentToLeague: item.canAddTournamentToLeague ?? false,
    canAddTeamToLeague: item.canAddTeamToLeague ?? false,
    canAddPlayerToTeam: item.canAddPlayerToTeam ?? false,
    canCreateDivisions: item.canCreateDivisions ?? false,
    umpireLeagueStatus: null,
    groundsRegistered: item.groundsRegistered ?? 0,
    sponsorsRegistered: item.sponsorsRegistered ?? 0,
    allowPlayersToJoin: item.allowPlayersToJoin ?? false,
    umpire: false,
  }) as IGetAllLeagues;
